import { toggleHud } from '../shared/hud-toggle.js';
import { isMuted, setMuted } from '../../audio/sounds.js';

export interface HudOptions {
  statusText(): string | null;
  onUndo(): void;
  onNewGame(): void;
  onDailyGame(): void;
  onReplay(): void;
}

export interface Hud {
  recordMove(): void;
  resetStats(): void;
  refreshStatus(): void;
  setUndoEnabled(enabled: boolean): void;
  showWin(): void;
  showLose(): void;
  hideWin(): void;
}

export function createHud(opts: HudOptions): Hud {
  const movesEl = document.getElementById('moves')!;
  const timeEl = document.getElementById('time')!;
  const statusEl = document.getElementById('status')!;
  const winOverlay = document.getElementById('win-overlay')!;
  const winMovesEl = document.getElementById('win-moves')!;
  const winTimeEl = document.getElementById('win-time')!;
  const winTitleEl = document.getElementById('win-title');
  const undoBtn = document.getElementById('undo') as HTMLButtonElement | null;

  let movesCount = 0;
  let startedAt = 0;
  let timerStarted = false;

  function tickClock() {
    movesEl.textContent = `${movesCount} ${movesCount === 1 ? 'move' : 'moves'}`;
    const sec = timerStarted ? Math.floor((performance.now() - startedAt) / 1000) : 0;
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    timeEl.textContent = `${mm}:${ss.toString().padStart(2, '0')}`;
  }
  setInterval(tickClock, 250);
  tickClock();

  document.getElementById('new-game')?.addEventListener('click', () => opts.onNewGame());
  document.getElementById('daily-game')?.addEventListener('click', () => opts.onDailyGame());
  document.getElementById('win-replay')?.addEventListener('click', () => opts.onReplay());
  document.getElementById('hud-toggle')?.addEventListener('click', () => toggleHud());
  undoBtn?.addEventListener('click', () => opts.onUndo());

  // Mute toggle - swaps the speaker icon and persists across sessions.
  const muteBtn = document.getElementById('mute-toggle');
  function syncMute() {
    muteBtn?.querySelector('use')?.setAttribute('href', isMuted() ? '#icon-mute' : '#icon-sound');
    const label = isMuted() ? 'Unmute' : 'Mute';
    muteBtn?.setAttribute('aria-label', label);
    muteBtn?.setAttribute('title', label);
  }
  muteBtn?.addEventListener('click', () => { setMuted(!isMuted()); syncMute(); });
  syncMute();

  // How-to-play modal (content rendered from the page's markdown).
  const rulesModal = document.getElementById('rules-modal');
  const closeRules = () => { if (rulesModal) rulesModal.hidden = true; };
  document.getElementById('rules-btn')?.addEventListener('click', () => {
    if (rulesModal) rulesModal.hidden = false;
  });
  document.getElementById('rules-close')?.addEventListener('click', closeRules);
  rulesModal?.addEventListener('click', (e) => { if (e.target === rulesModal) closeRules(); });
  window.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeRules(); });
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      opts.onUndo();
    }
  });

  const hud: Hud = {
    recordMove() {
      movesCount++;
      if (!timerStarted) {
        timerStarted = true;
        startedAt = performance.now();
      }
      tickClock();
    },
    resetStats() {
      movesCount = 0;
      timerStarted = false;
      startedAt = 0;
      statusEl.classList.remove('show');
      hud.hideWin();
      tickClock();
    },
    refreshStatus() {
      const text = opts.statusText();
      if (text) {
        statusEl.textContent = text;
        statusEl.classList.add('show');
      } else {
        statusEl.classList.remove('show');
      }
    },
    setUndoEnabled(enabled) {
      if (undoBtn) undoBtn.disabled = !enabled;
    },
    showWin() {
      if (winTitleEl) winTitleEl.textContent = 'You won';
      winMovesEl.textContent = String(movesCount);
      winTimeEl.textContent = timeEl.textContent ?? '0:00';
      winOverlay.classList.remove('win-overlay--lose');
      winOverlay.hidden = false;
    },
    showLose() {
      if (winTitleEl) winTitleEl.textContent = 'No moves left';
      winMovesEl.textContent = String(movesCount);
      winTimeEl.textContent = timeEl.textContent ?? '0:00';
      winOverlay.classList.add('win-overlay--lose');
      winOverlay.hidden = false;
    },
    hideWin() {
      winOverlay.classList.remove('win-overlay--lose');
      winOverlay.hidden = true;
    },
  };
  return hud;
}
